import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Package, Tag, Layers, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { get, patch } from "@/lib/api-client";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Tabs } from "@/components/ui/Tabs";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatCurrency } from "@/lib/format";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"; // 🛠️ Import Dialog
import { ProductFormModal } from "./ProductFormModal";
import { CategoryFormModal } from "./CategoryFormModal";
import { VariantFormModal } from "./VariantFormModal";
import type { Category, Product } from "@/types/catalog";

export function CatalogAdminPage() {
  const [tab, setTab] = useState("products");
  const [productModal, setProductModal] = useState<Product | null | "new">(null);
  const [categoryModal, setCategoryModal] = useState<Category | null | "new">(null);
  const [variantModal, setVariantModal] = useState<{ product: Product } | null>(null);
  
  // 🛠️ NEW: State to track which product to delete
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null); 
  const qc = useQueryClient();

  const categories = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => get<Category[]>("/catalog/categories"),
  });
  const products = useQuery({
    queryKey: ["admin-products"],
    queryFn: () => get<Product[]>("/catalog/products"),
  });

  // 🛠️ NEW: Deletion Mutation (Soft Delete by setting status to inactive)
  const deleteProduct = useMutation({
    mutationFn: (id: number) => patch(`/admin/products/${id}`, { status: "inactive" }),
    onSuccess: () => {
      toast.success("Product has been deactivated (soft deleted).");
      qc.invalidateQueries({ queryKey: ["admin-products"] });
      setDeleteTarget(null);
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Catalog Management</h1>
          <p className="text-sm text-surface-500 mt-1">
            Categories, products, and variants. Deactivate rather than delete to preserve history.
          </p>
        </div>
        <div className="flex gap-2">
          {tab === "products" && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setProductModal("new")}>
              New product
            </Button>
          )}
          {tab === "categories" && (
            <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal("new")}>
              New category
            </Button>
          )}
        </div>
      </div>

      <Card>
        <div className="px-5 pt-3">
          <Tabs value={tab} onChange={setTab}
                tabs={[
                  { value: "products",   label: "Products",   count: products.data?.length   },
                  { value: "categories", label: "Categories", count: categories.data?.length },
                ]} />
        </div>
        <CardBody className="p-0">
          {tab === "products" && (
            products.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : products.data && products.data.length ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Code</TH><TH>Name</TH><TH>Category</TH>
                    <TH>Price</TH><TH>Points</TH><TH>Variants</TH>
                    <TH>Status</TH><TH className="text-right">Actions</TH>
                  </TR>
                </THead>
                <tbody>
                  {products.data.map((p) => (
                    <TR key={p.id}>
                      <TD className="font-mono text-xs">{p.product_code}</TD>
                      <TD className="font-medium">{p.name}</TD>
                      <TD className="text-xs">{p.category.name}</TD>
                      <TD>{formatCurrency(p.base_price)}</TD>
                      <TD>{p.qualification_point_value}</TD>
                      <TD>{p.variants.length}</TD>
                      <TD><StatusPill status={p.status} /></TD>
                      <TD>
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" leftIcon={<Layers className="h-3.5 w-3.5" />}
                                  onClick={() => setVariantModal({ product: p })}>
                            Variants
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setProductModal(p)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          {/* 🛠️ NEW: Delete Button */}
                          <Button size="sm" variant="ghost" disabled={p.status === "inactive"} onClick={() => setDeleteTarget(p)}>
                            <Trash2 className="h-4 w-4 text-danger-500 hover:text-danger-600" />
                          </Button>
                        </div>
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-8 text-center text-sm text-surface-500 flex flex-col items-center">
                <Package className="h-10 w-10 text-surface-300 mb-2" />
                No products yet.
              </div>
            )
          )}
          {tab === "categories" && (
            categories.isLoading ? (
              <div className="p-5 space-y-3">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : categories.data && categories.data.length ? (
              <Table>
                <THead>
                  <TR><TH>Name</TH><TH>Description</TH><TH>Order</TH><TH>Status</TH><TH></TH></TR>
                </THead>
                <tbody>
                  {categories.data.map((c) => (
                    <TR key={c.id}>
                      <TD className="font-medium">{c.name}</TD>
                      <TD className="text-xs text-surface-500 line-clamp-1">{c.description}</TD>
                      <TD>{c.display_order}</TD>
                      <TD><StatusPill status={c.status} /></TD>
                      <TD>
                        <Button size="sm" variant="ghost" onClick={() => setCategoryModal(c)}>Edit</Button>
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="p-8 text-center text-sm text-surface-500 flex flex-col items-center">
                <Tag className="h-10 w-10 text-surface-300 mb-2" />
                No categories yet.
              </div>
            )
          )}
        </CardBody>
      </Card>

      {productModal !== null && (
        <ProductFormModal
          open onClose={() => setProductModal(null)}
          product={productModal === "new" ? null : productModal}
          categories={categories.data ?? []}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-products"] });
            setProductModal(null);
          }}
        />
      )}
      {categoryModal !== null && (
        <CategoryFormModal
          open onClose={() => setCategoryModal(null)}
          category={categoryModal === "new" ? null : categoryModal}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-categories"] });
            setCategoryModal(null);
          }}
        />
      )}
      {variantModal && (
        <VariantFormModal
          open onClose={() => setVariantModal(null)}
          product={variantModal.product}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin-products"] });
            setVariantModal(null);
          }}
        />
      )}

      {/* 🛠️ NEW: Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Product?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? It will be hidden from the catalog.`}
        confirmLabel="Yes, Delete it"
        destructive
        loading={deleteProduct.isPending}
        onConfirm={() => { if (deleteTarget) deleteProduct.mutate(deleteTarget.id); }}
      />
    </div>
  );
}